import React, { useState, useEffect, useCallback } from 'react';
import {
  X,
  User,
  UserCheck,
  CreditCard,
  Package,
  MessageSquare,
  Send,
  Paperclip,
  Loader,
  ArrowLeft,
  RotateCcw,
  Building2
} from 'lucide-react';
import { getSaleById, addSaleComment, reverseSale } from '../../api/sales';
import { generateReceipt } from '../../api/receipts';
import { toast } from 'react-toastify';
import ReceiptActions from '../receipts/ReceiptActions';
import { useAuth } from '../../contexts/AuthContext';

const SaleDetailModal = ({ saleId, onClose, onReverse }) => {
  const { user } = useAuth();
  const isAdmin = ['admin', 'super_admin', 'retail_admin', 'branch_admin', 'system_admin'].includes(user?.role);
  const [sale, setSale] = useState(null);
  const [loading, setLoading] = useState(true);
  const [commentText, setCommentText] = useState('');
  const [commentFiles, setCommentFiles] = useState([]);
  const [submittingComment, setSubmittingComment] = useState(false);
  const [showReceiptPreview, setShowReceiptPreview] = useState(false);
  const [receiptUrl, setReceiptUrl] = useState(null);

  const fetchSaleDetails = useCallback(async () => {
    if (!saleId) return;

    setLoading(true);
    try {
      const response = await getSaleById(saleId);
      setSale(response.data);
    } catch (error) {
      toast.error('Failed to load sale details');
      // Don't close immediately, let user see the error and close manually
      setLoading(false);
    } finally {
      setLoading(false);
    }
  }, [saleId]);

  useEffect(() => {
    if (saleId) {
      fetchSaleDetails();
    }
  }, [fetchSaleDetails, saleId]);

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setCommentFiles(prev => [...prev, ...files]);
  };

  const removeFile = (index) => {
    setCommentFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleReverse = async () => {
    if (window.confirm(`Are you sure you want to reverse this sale? This will restore stock and reverse payments.`)) {
      try {
        await reverseSale(saleId);
        toast.success('Sale reversed successfully');
        if (onReverse) onReverse();
      } catch (error) {
        toast.error(error.response?.data?.message || 'Failed to reverse sale');
      }
    }
  };

  const handleSubmitComment = async (e) => {
    e.preventDefault();

    if (!commentText.trim() && commentFiles.length === 0) {
      toast.error('Please enter a comment or attach files');
      return;
    }

    setSubmittingComment(true);

    try {
      const formData = new FormData();
      formData.append('comment_text', commentText.trim());

      commentFiles.forEach((file) => {
        formData.append('attachments', file);
      });

      const response = await addSaleComment(saleId, formData);

      toast.success('Comment added successfully');
      setCommentText('');
      setCommentFiles([]);
      fetchSaleDetails();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to add comment');
    } finally {
      setSubmittingComment(false);
    }
  };

  const getPaymentStatusColor = (status) => {
    const colors = {
      paid: 'text-green-600 bg-green-100',
      partial: 'text-yellow-600 bg-yellow-100',
      unpaid: 'text-red-600 bg-red-100'
    };
    return colors[status] || 'text-gray-600 bg-gray-100';
  };

  const handlePreviewReceipt = async () => {
    try {
      const blob = await generateReceipt(saleId, false);
      const url = window.URL.createObjectURL(blob);
      setReceiptUrl(url);
      setShowReceiptPreview(true);
    } catch (error) {
      toast.error('Failed to load receipt preview');
    }
  };

  const handleClosePreview = () => {
    if (receiptUrl) {
      window.URL.revokeObjectURL(receiptUrl);
      setReceiptUrl(null);
    }
    setShowReceiptPreview(false);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (receiptUrl) {
        window.URL.revokeObjectURL(receiptUrl);
      }
    };
  }, [receiptUrl]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4 overflow-y-auto" onClick={(e) => {
      // Close modal when clicking on backdrop
      if (e.target === e.currentTarget) {
        onClose();
      }
    }}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[95vh] overflow-y-auto my-4">
        {loading ? (
          <div className="p-8">
            <div className="text-center">
              <Loader className="w-12 h-12 text-accent-gold animate-spin mx-auto mb-4" />
              <p className="text-secondary-gray">Loading sale details...</p>
            </div>
          </div>
        ) : !sale ? (
          <div className="p-8">
            <div className="text-center">
              <p className="text-red-600 mb-4">Failed to load sale details</p>
              <button
                onClick={onClose}
                className="px-4 py-2 bg-accent-gold text-white rounded-lg hover:bg-yellow-600"
              >
                Close
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="sticky top-0 bg-white border-b border-secondary-lightGray px-4 sm:px-6 py-4 flex items-center justify-between z-10">
              <div className="flex items-center gap-3 flex-1 min-w-0">
                {showReceiptPreview && (
                  <button
                    onClick={handleClosePreview}
                    className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
                    title="Back to Details"
                  >
                    <ArrowLeft className="w-5 h-5 sm:w-6 sm:h-6" />
                  </button>
                )}
                <div className="flex-1 min-w-0">
                  <h2 className="text-lg sm:text-xl font-bold text-primary-black truncate">
                    {showReceiptPreview ? 'Receipt Preview' : 'Sale Details'}
                  </h2>
                  <p className="text-sm sm:text-base text-accent-gold font-semibold">{sale.receipt_number}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 sm:gap-3 ml-4">
                {!showReceiptPreview && (
                  <>
                    {isAdmin && sale.status !== 'cancelled' && (
                      <button
                        onClick={handleReverse}
                        className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors flex-shrink-0"
                        title="Reverse Sale"
                      >
                        <RotateCcw className="w-5 h-5 sm:w-6 sm:h-6" />
                      </button>
                    )}
                    <ReceiptActions sale={sale} onPreview={handlePreviewReceipt} />
                  </>
                )}
                <button
                  onClick={onClose}
                  className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
                  title="Close"
                >
                  <X className="w-5 h-5 sm:w-6 sm:h-6" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-4 sm:p-6">
              {showReceiptPreview ? (
                /* Receipt Preview */
                <div className="w-full h-[70vh]">
                  <iframe
                    src={receiptUrl}
                    className="w-full h-full border-0"
                    title="Receipt Preview"
                  />
                </div>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
                  {/* Left Column - Sale Items & Details */}
                  <div className="lg:col-span-2 space-y-4 sm:space-y-6">
              {/* Sale Info Bar */}
              <div className="bg-secondary-offWhite rounded-lg p-3 sm:p-4">
                <div className="flex flex-wrap gap-3 sm:gap-4 text-xs sm:text-sm">
                  <div>
                    <span className="text-secondary-gray">Sale Date: </span>
                    <span className="font-semibold text-primary-black">
                      {new Date(sale.sale_date).toLocaleDateString()} {new Date(sale.sale_date).toLocaleTimeString()}
                    </span>
                  </div>
                  <div>
                    <span className="text-secondary-gray">Items: </span>
                    <span className="font-semibold text-primary-black">{sale.items?.length || 0}</span>
                  </div>
                </div>
              </div>

              {/* Sale Items */}
              <div className="bg-white border border-secondary-lightGray rounded-lg overflow-hidden">
                <div className="bg-primary-black text-white px-3 sm:px-4 py-2 sm:py-3 flex items-center gap-2">
                  <Package className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
                  <h3 className="text-sm sm:text-base font-semibold">Sale Items</h3>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full min-w-[500px]">
                    <thead className="bg-secondary-offWhite">
                      <tr>
                        <th className="px-3 sm:px-4 py-2 sm:py-3 text-left text-xs sm:text-sm font-semibold text-primary-black">Product</th>
                        <th className="px-3 sm:px-4 py-2 sm:py-3 text-right text-xs sm:text-sm font-semibold text-primary-black">Price</th>
                        <th className="px-3 sm:px-4 py-2 sm:py-3 text-center text-xs sm:text-sm font-semibold text-primary-black">Qty</th>
                        <th className="px-3 sm:px-4 py-2 sm:py-3 text-right text-xs sm:text-sm font-semibold text-primary-black">Discount</th>
                        <th className="px-3 sm:px-4 py-2 sm:py-3 text-right text-xs sm:text-sm font-semibold text-primary-black">Total</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-secondary-lightGray">
                      {sale.items && sale.items.map((item, index) => (
                        <tr key={index}>
                          <td className="px-3 sm:px-4 py-2 sm:py-3">
                            <p className="font-medium text-primary-black text-xs sm:text-sm">{item.product_name}</p>
                            <p className="text-xs text-secondary-gray">{item.product_code}</p>
                          </td>
                          <td className="px-3 sm:px-4 py-2 sm:py-3 text-right text-xs sm:text-sm">
                            ₦{parseFloat(item.selling_price).toLocaleString()}
                          </td>
                          <td className="px-3 sm:px-4 py-2 sm:py-3 text-center text-xs sm:text-sm">
                            {item.quantity}
                          </td>
                          <td className="px-3 sm:px-4 py-2 sm:py-3 text-right text-red-600 text-xs sm:text-sm">
                            {item.unit_discount > 0 ? `-₦${parseFloat(item.unit_discount).toLocaleString()}` : '-'}
                          </td>
                          <td className="px-3 sm:px-4 py-2 sm:py-3 text-right font-semibold text-accent-gold text-xs sm:text-sm">
                            ₦{parseFloat(item.line_subtotal).toLocaleString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Totals */}
                <div className="border-t-2 border-secondary-lightGray bg-secondary-offWhite p-3 sm:p-4">
                  <div className="space-y-1 sm:space-y-2 max-w-md sm:ml-auto">
                    <div className="flex justify-between text-xs sm:text-sm">
                      <span className="text-secondary-gray">Subtotal:</span>
                      <span className="font-medium">₦{parseFloat(sale.subtotal).toLocaleString()}</span>
                    </div>
                    {sale.total_discount > 0 && (
                      <div className="flex justify-between text-xs sm:text-sm">
                        <span className="text-secondary-gray">Discount:</span>
                        <span className="font-medium text-red-600">-₦{parseFloat(sale.total_discount).toLocaleString()}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-base sm:text-lg font-bold pt-2 border-t border-secondary-lightGray">
                      <span className="text-primary-black">Total Amount:</span>
                      <span className="text-accent-gold">₦{parseFloat(sale.total_amount).toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between text-xs sm:text-sm pt-2 border-t border-secondary-lightGray">
                      <span className="text-secondary-gray">Amount Paid:</span>
                      <span className="font-medium text-green-600">₦{parseFloat(sale.amount_paid).toLocaleString()}</span>
                    </div>
                    {sale.amount_due > 0 && (
                      <div className="flex justify-between text-xs sm:text-sm">
                        <span className="text-secondary-gray">Amount Due:</span>
                        <span className="font-bold text-red-600">₦{parseFloat(sale.amount_due).toLocaleString()}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Comments Section */}
              <div className="bg-white border border-secondary-lightGray rounded-lg overflow-hidden">
                <div className="bg-primary-black text-white px-3 sm:px-4 py-2 sm:py-3 flex items-center gap-2">
                  <MessageSquare className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
                  <h3 className="text-sm sm:text-base font-semibold">Comments & Notes</h3>
                </div>

                <div className="p-3 sm:p-4">
                  {/* Add Comment Form */}
                  <form onSubmit={handleSubmitComment} className="mb-4">
                    <textarea
                      value={commentText}
                      onChange={(e) => setCommentText(e.target.value)}
                      rows={3}
                      className="w-full px-3 py-2 border border-secondary-lightGray rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-gold resize-none mb-2 text-xs sm:text-sm"
                      placeholder="Add a comment or note about this sale..."
                    />

                    {/* File Attachments */}
                    {commentFiles.length > 0 && (
                      <div className="mb-3 space-y-2">
                        {commentFiles.map((file, index) => (
                          <div key={index} className="flex items-center justify-between p-2 bg-secondary-offWhite rounded">
                            <span className="text-xs sm:text-sm text-secondary-gray truncate flex-1">{file.name}</span>
                            <button
                              type="button"
                              onClick={() => removeFile(index)}
                              className="ml-2 p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded transition-colors flex-shrink-0"
                              title="Remove file"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="flex flex-col sm:flex-row gap-2">
                      <label className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-white border border-secondary-lightGray rounded-lg text-secondary-gray hover:bg-secondary-offWhite transition-colors cursor-pointer">
                        <Paperclip className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
                        <span className="text-xs sm:text-sm whitespace-nowrap">Attach Files</span>
                        <input
                          type="file"
                          multiple
                          onChange={handleFileChange}
                          className="hidden"
                        />
                      </label>
                      <button
                        type="submit"
                        disabled={submittingComment}
                        className="flex items-center justify-center gap-2 px-4 sm:px-6 py-2 bg-accent-gold text-white rounded-lg hover:bg-yellow-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Send className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
                        <span className="text-xs sm:text-sm whitespace-nowrap">{submittingComment ? 'Posting...' : 'Post Comment'}</span>
                      </button>
                    </div>
                  </form>

                  {/* Existing Comments */}
                  <div className="space-y-3">
                    {sale.comments && sale.comments.length > 0 ? (
                      sale.comments.map((comment) => (
                        <div key={comment.id} className="border border-secondary-lightGray rounded-lg p-3">
                          <div className="flex items-start justify-between mb-2">
                            <p className="font-semibold text-primary-black text-xs sm:text-sm">{comment.user_name}</p>
                            <p className="text-xs text-secondary-gray">
                              {new Date(comment.created_at).toLocaleString()}
                            </p>
                          </div>
                          <p className="text-secondary-gray text-xs sm:text-sm mb-2">{comment.comment_text}</p>
                          {comment.attachments && comment.attachments.length > 0 && (
                            <div className="flex flex-wrap gap-2 mt-2">
                              {comment.attachments.map((file, idx) => (
                                <a
                                  key={idx}
                                  href={file.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-xs px-2 py-1 bg-accent-gold text-white rounded hover:bg-yellow-600"
                                >
                                  {file.filename}
                                </a>
                              ))}
                            </div>
                          )}
                        </div>
                      ))
                    ) : (
                      <p className="text-center text-secondary-gray py-4 text-xs sm:text-sm">No comments yet</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Customer & Payment Info */}
            <div className="space-y-4 sm:space-y-6">
              {/* Customer Info */}
              <div className="bg-white border border-secondary-lightGray rounded-lg p-3 sm:p-4">
                <div className="flex items-center gap-2 mb-3">
                  <User className="w-4 h-4 sm:w-5 sm:h-5 text-accent-gold flex-shrink-0" />
                  <h3 className="text-sm sm:text-base font-semibold text-primary-black">Customer Information</h3>
                </div>
                <div className="space-y-2 text-xs sm:text-sm">
                  <div>
                    <p className="text-secondary-gray">Name</p>
                    <p className="font-medium text-primary-black">{sale.customer_name}</p>
                  </div>
                  <div>
                    <p className="text-secondary-gray">Phone</p>
                    <p className="font-medium text-primary-black">{sale.customer_phone}</p>
                  </div>
                  {sale.customer_email && (
                    <div>
                      <p className="text-secondary-gray">Email</p>
                      <p className="font-medium text-primary-black">{sale.customer_email}</p>
                    </div>
                  )}
                  {sale.customer_address && (
                    <div>
                      <p className="text-secondary-gray">Address</p>
                      <p className="font-medium text-primary-black">{sale.customer_address}</p>
                    </div>
                  )}
                </div>
              </div>

            {/* Branch Info */}
            {sale.branch_name && (
              <div className="bg-white border border-secondary-lightGray rounded-lg p-3 sm:p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Building2 className="w-4 h-4 sm:w-5 sm:h-5 text-accent-gold flex-shrink-0" />
                  <h3 className="text-sm sm:text-base font-semibold text-primary-black">Branch Information</h3>
                </div>
                <div className="space-y-2 text-xs sm:text-sm">
                  <div>
                    <p className="text-secondary-gray">Branch Name</p>
                    <p className="font-medium text-primary-black">{sale.branch_name}</p>
                  </div>
                  {sale.branch_code && (
                    <div>
                      <p className="text-secondary-gray">Branch Code</p>
                      <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {sale.branch_code}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}

              {/* Referrer Info */}
              {sale.referrer_name && (
                <div className="bg-white border border-secondary-lightGray rounded-lg p-3 sm:p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <UserCheck className="w-4 h-4 sm:w-5 sm:h-5 text-accent-gold flex-shrink-0" />
                    <h3 className="text-sm sm:text-base font-semibold text-primary-black">Referrer Information</h3>
                  </div>
                  <div className="space-y-2 text-xs sm:text-sm">
                    <div>
                      <p className="text-secondary-gray">Name</p>
                      <p className="font-medium text-primary-black">{sale.referrer_name}</p>
                    </div>
                    <div>
                      <p className="text-secondary-gray">Commission Rate</p>
                      <p className="font-medium text-accent-gold">{sale.commission_rate}%</p>
                    </div>
                    {sale.commission_amount > 0 && (
                      <div>
                        <p className="text-secondary-gray">Commission Earned</p>
                        <p className="font-bold text-green-600">₦{parseFloat(sale.commission_amount).toLocaleString()}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Payment Info */}
              <div className="bg-white border border-secondary-lightGray rounded-lg p-3 sm:p-4">
                <div className="flex items-center gap-2 mb-3">
                  <CreditCard className="w-4 h-4 sm:w-5 sm:h-5 text-accent-gold flex-shrink-0" />
                  <h3 className="text-sm sm:text-base font-semibold text-primary-black">Payment Information</h3>
                </div>
                <div className="space-y-3">
                  <div>
                    <p className="text-xs sm:text-sm text-secondary-gray mb-1">Payment Method</p>
                    <span className="px-2 sm:px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs sm:text-sm font-medium">
                      {sale.payment_method.toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <p className="text-xs sm:text-sm text-secondary-gray mb-1">Payment Status</p>
                    <span className={`px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium ${getPaymentStatusColor(sale.payment_status)}`}>
                      {sale.payment_status.charAt(0).toUpperCase() + sale.payment_status.slice(1)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Profit Info */}
              {sale.gross_profit !== undefined && (
                <div className="bg-white border border-secondary-lightGray rounded-lg p-3 sm:p-4">
                  <h3 className="text-sm sm:text-base font-semibold text-primary-black mb-3">Profit Analysis</h3>
                  <div className="space-y-2 text-xs sm:text-sm">
                    <div className="flex justify-between">
                      <span className="text-secondary-gray">Cost:</span>
                      <span className="font-medium">₦{parseFloat(sale.total_cost || 0).toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-secondary-gray">{parseFloat(sale.gross_profit) >= 0 ? 'Gross Profit:' : 'Gross Loss:'}</span>
                      <span className={`font-medium ${parseFloat(sale.gross_profit) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        ₦{parseFloat(sale.gross_profit).toLocaleString()}
                      </span>
                    </div>
                    {sale.commission_amount > 0 && (
                      <div className="flex justify-between">
                        <span className="text-secondary-gray">Commission:</span>
                        <span className="font-medium text-yellow-600">-₦{parseFloat(sale.commission_amount).toLocaleString()}</span>
                      </div>
                    )}
                    <div className="flex justify-between pt-2 border-t border-secondary-lightGray">
                      <span className="font-semibold text-primary-black">{parseFloat(sale.net_profit || sale.gross_profit) >= 0 ? 'Net Profit:' : 'Net Loss:'}</span>
                      <span className={`font-bold ${parseFloat(sale.net_profit || sale.gross_profit) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        ₦{parseFloat(sale.net_profit || sale.gross_profit).toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Notes */}
              {sale.notes && (
                <div className="bg-white border border-secondary-lightGray rounded-lg p-3 sm:p-4">
                  <h3 className="text-sm sm:text-base font-semibold text-primary-black mb-2">Notes</h3>
                  <p className="text-xs sm:text-sm text-secondary-gray">{sale.notes}</p>
                </div>
              )}

              {/* Salesperson Signature */}
              {sale.created_by_name && (
                <div className="bg-white border border-secondary-lightGray rounded-lg p-3 sm:p-4">
                  <h3 className="text-sm sm:text-base font-semibold text-primary-black mb-2">Processed By</h3>
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4 text-accent-gold" />
                    <p className="text-xs sm:text-sm font-medium text-primary-black">{sale.created_by_name}</p>
                  </div>
                  <p className="text-xs text-secondary-gray mt-1">Sales Representative</p>
                </div>
              )}
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SaleDetailModal;
